"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { useTranslations } from "@/i18n"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControlledField,
  Input,
} from "@ibada/ui"

import { ChartAccountKind } from "@/features/finance/model/accounting-reports-types"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileFormError, MobileFormInputRow, MobileFormSection, MobileFormSheet } from "@/shared/components/mobile-form"
import { useCreateAccountingAccountMutation } from "@/features/finance/services/finance-query"

export type FinanceChartAccountFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** INCOME → a REVENUE account (gets an eager SYSTEM_LINKED fund); EXPENSE → an OPERATING_EXPENSE account. */
  kind: ChartAccountKind
  workspaceId: string | null
  /**
   * Set to create an account this BRANCH owns rather than a workspace-wide one (ADR-0024 Phase 4).
   * Only a workspace under BRANCH_AUTONOMY may pass it; the server refuses it under FULL_PARISH.
   */
  owningBranchId?: string | null
  /** Fired after a successful create so the caller can refetch the chart. */
  onSaved?: () => void
}

type ChartAccountFormValues = { name: string }

/**
 * Create a first-class income/expense account directly in the Chart of Accounts (categories→accounts
 * convergence). Just a name — the classification (and thus GL type) is derived from `kind` on the server, and an
 * income account's SYSTEM_LINKED fund is created there too.
 *
 * Two owners are possible. Without `owningBranchId` the account is workspace-wide, which is the only kind
 * that exists under FULL_PARISH and the only kind the parish creates. With it, the account belongs to that
 * branch alone — what BRANCH_AUTONOMY actually grants. The server enforces both rules.
 */
export function FinanceChartAccountFormDialog({
  open,
  onOpenChange,
  kind,
  workspaceId,
  owningBranchId,
  onSaved,
}: FinanceChartAccountFormDialogProps) {
  const t = useTranslations("finance.accountsChart.createDialog")
  const { canEdit } = useModulePermission(WorkspaceAccessModule.FINANCE)

  const schema = React.useMemo(
    () => z.object({ name: z.string().trim().min(1, t("nameRequired")).max(120) }),
    [t],
  )
  const form = useForm<ChartAccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "" },
  })
  const mutation = useCreateAccountingAccountMutation()

  React.useEffect(() => {
    if (open) form.reset({ name: "" })
    // form is a stable ref — reset only when the dialog (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const isIncome = kind === ChartAccountKind.Income

  const onSubmit = form.handleSubmit(async (values) => {
    if (!canEdit) return
    if (!workspaceId) return
    try {
      await mutation.mutateAsync({ workspaceId, kind, name: values.name, owningBranchId })
      toast.success(t("success"))
      onOpenChange(false)
      onSaved?.()
    } catch {
      toast.error(t("error"))
    }
  })

  const isMobile = useIsMobile()

  if (!canEdit) return null

  // Phone: the app's edit sheet, the name as one row and what it is for as the note under it.
  if (isMobile) {
    return (
      <MobileFormSheet
        open={open}
        onOpenChange={onOpenChange}
        title={isIncome ? t("incomeTitle") : t("expenseTitle")}
        note={isIncome ? t("incomeDescription") : t("expenseDescription")}
        cancelLabel={t("cancel")}
        saveLabel={t("submit")}
        onSave={() => void onSubmit()}
        saving={mutation.isPending}
        saveDisabled={!workspaceId}
      >
        <div>
          <MobileFormSection>
            <MobileFormInputRow
              label={t("nameLabel")}
              value={form.watch("name")}
              onChange={(event) => form.setValue("name", event.target.value, { shouldValidate: form.formState.isSubmitted })}
              placeholder={t("namePlaceholder")}
              maxLength={120}
              invalid={Boolean(form.formState.errors.name)}
            />
          </MobileFormSection>
          <MobileFormError message={form.formState.errors.name?.message} />
        </div>
      </MobileFormSheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={onSubmit} className='space-y-4'>
            <DialogHeader>
              <DialogTitle>{isIncome ? t("incomeTitle") : t("expenseTitle")}</DialogTitle>
              <DialogDescription>
                {isIncome ? t("incomeDescription") : t("expenseDescription")}
              </DialogDescription>
            </DialogHeader>
            <FormControlledField
              control={form.control}
              name='name'
              label={t("nameLabel")}
              renderInput={({ field }) => (
                <Input
                  {...field}
                  placeholder={t("namePlaceholder")}
                  disabled={mutation.isPending}
                  maxLength={120}
                  showCount
                />
              )}
            />
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                {t("cancel")}
              </Button>
              <Button type='submit' disabled={mutation.isPending || !workspaceId}>
                {mutation.isPending ? t("submitting") : t("submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
